import dayjs, { type Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { EntityType } from '../types';

dayjs.extend(isoWeek);
dayjs.extend(quarterOfYear);

/** Vertical space allocated to each entry's row in the Timeline view. */
export const TIMELINE_ROW_HEIGHT = 120;

export const TIMELINE_CARD_MIN_WIDTH = 190;

/**
 * Controls whether/when a TimelineReferenceCard shows properties beyond its title and date field:
 * `collapsed` never shows them, `expanded` always shows them, `expandable` shows them only while the
 * card is selected (collapsing again otherwise).
 */
export type TimelineCardExpansionMode = 'collapsed' | 'expanded' | 'expandable';

/** Timeline zoom levels, from most to least granular — each vertical column represents one unit of this scale. */
export type TimelineScale = 'D' | 'W' | 'M' | 'Q' | 'Y';
export const TIMELINE_SCALES: TimelineScale[] = ['D', 'W', 'M', 'Q', 'Y'];
export const TIMELINE_SCALE_LABELS: Record<TimelineScale, string> = {
	D: 'Day',
	W: 'Week',
	M: 'Month',
	Q: 'Quarter',
	Y: 'Year',
};

/** Pixel width of a single column in the Timeline view, at each scale. */
export const TIMELINE_COLUMN_WIDTH: Record<TimelineScale, number> = {
	// Must stay a multiple of `KNITTING_TILE_PX` so the weekend pattern runs seamlessly across adjacent days.
	D: 120,
	W: 240,
	M: 240,
	Q: 240,
	Y: 240,
};

/** How many columns before/after today get rendered, at each scale. */
export const TIMELINE_SCALE_RANGE: Record<TimelineScale, { before: number; after: number }> = {
	D: { before: 30, after: 90 },
	W: { before: 12, after: 26 },
	M: { before: 6, after: 18 },
	Q: { before: 4, after: 8 },
	Y: { before: 3, after: 5 },
};

function startOfScaleUnit(date: Dayjs, scale: TimelineScale): Dayjs {
	switch (scale) {
	case 'D': return date.startOf('day');
	case 'W': return date.startOf('isoWeek');
	case 'M': return date.startOf('month');
	case 'Q': return date.startOf('quarter');
	case 'Y': return date.startOf('year');
	}
}

/** Whole-unit difference between `date` and `origin`, at the given scale (can be negative). */
export function unitIndexFromDate(date: Dayjs, origin: Dayjs, scale: TimelineScale): number {
	const alignedDate = startOfScaleUnit(date, scale);
	const alignedOrigin = startOfScaleUnit(origin, scale);
	switch (scale) {
	case 'D': return alignedDate.diff(alignedOrigin, 'day');
	case 'W': return alignedDate.diff(alignedOrigin, 'day') / 7;
	case 'M': return alignedDate.diff(alignedOrigin, 'month');
	case 'Q': return alignedDate.diff(alignedOrigin, 'month') / 3;
	case 'Y': return alignedDate.diff(alignedOrigin, 'year');
	}
}

function addScaleUnits(date: Dayjs, count: number, scale: TimelineScale): Dayjs {
	switch (scale) {
	case 'D': return date.add(count, 'day');
	case 'W': return date.add(count * 7, 'day');
	case 'M': return date.add(count, 'month');
	case 'Q': return date.add(count * 3, 'month');
	case 'Y': return date.add(count, 'year');
	}
}

/** The scale-unit boundary `unitIndex` units after `origin`'s own unit boundary. */
export function dateFromUnitIndex(origin: Dayjs, unitIndex: number, scale: TimelineScale): Dayjs {
	return addScaleUnits(startOfScaleUnit(origin, scale), unitIndex, scale);
}

/** How far through its scale unit `date` falls, from 0 (unit start) up to but excluding 1. */
export function unitFractionFromDate(date: Dayjs, scale: TimelineScale): number {
	const unitStart = startOfScaleUnit(date, scale);
	return date.diff(unitStart) / addScaleUnits(unitStart, 1, scale).diff(unitStart);
}

/** Fractional unit position of `date` relative to `origin`'s unit: whole units plus how far through its unit it falls. */
export function unitPositionFromDate(date: Dayjs, origin: Dayjs, scale: TimelineScale): number {
	return unitIndexFromDate(date, origin, scale) + unitFractionFromDate(date, scale);
}

/** Inverse of `unitPositionFromDate`. */
export function dateFromUnitPosition(origin: Dayjs, position: number, scale: TimelineScale): Dayjs {
	const unitIndex = Math.floor(position);
	const unitStart = dateFromUnitIndex(origin, unitIndex, scale);
	const unitDurationMs = dateFromUnitIndex(origin, unitIndex + 1, scale).diff(unitStart);
	return unitStart.add((position - unitIndex) * unitDurationMs, 'ms');
}

/** Label for the period one scale coarser than `scale` that contains `date`, always including the year. */
export function formatTimelineOverviewLabel(date: Dayjs, scale: TimelineScale): string {
	switch (scale) {
	case 'D': {
		const weekStart = date.startOf('isoWeek');
		return `Week ${Math.ceil(weekStart.date() / 7)} of ${weekStart.format('MMM YYYY')}`;
	}
	case 'W': return date.format('MMMM YYYY');
	case 'M':
	case 'Q': return date.format('YYYY');
	case 'Y': return `${Math.floor(date.year() / 10) * 10}s`;
	}
}

/** Column header label for the unit starting at `date`, at the given scale. */
export function formatTimelineColumnLabel(date: Dayjs, scale: TimelineScale): string {
	switch (scale) {
	case 'D': return date.format('ddd D MMM');
	case 'W': return `Week ${Math.ceil(date.date() / 7)} of ${date.format(date.isSame(dayjs(), 'year') ? 'MMM' : 'MMM YYYY')}`;
	case 'M': return date.format('MMMM YYYY');
	case 'Q': return `Q${date.quarter()} ${date.format('YYYY')}`;
	case 'Y': return date.format('YYYY');
	}
}

const pluralize = (count: number, unit: string) => `${count} ${unit}${count === 1 ? '' : 's'}`;

/** Relative-date label for a selected slot: days under a week, weeks under 6 months, months under a year, then years and months, then years from 3 years. */
export function formatSlotRelativeLabel(date: Dayjs, origin: Dayjs): string {
	const day = date.startOf('day');
	const originDay = origin.startOf('day');
	const diffDays = day.diff(originDay, 'day');
	if (diffDays === 0) return 'Today';
	if (diffDays === 1) return 'Tomorrow';
	if (diffDays === -1) return 'Yesterday';
	const isFuture = diffDays > 0;
	const relative = (span: string) => isFuture ? `in ${span}` : `${span} ago`;
	const absDays = Math.abs(diffDays);
	if (absDays < 7) {
		return relative(pluralize(absDays, 'day'));
	}
	const absMonths = Math.abs(day.diff(originDay, 'month'));
	if (absMonths < 6) {
		const weeks = Math.round(absDays / 7);
		if (weeks === 1) return isFuture ? 'Next week' : 'Last week';
		return relative(pluralize(weeks, 'week'));
	}
	if (absMonths < 12) {
		return relative(pluralize(absMonths, 'month'));
	}
	const years = Math.floor(absMonths / 12);
	const remainderMonths = absMonths % 12;
	return relative(remainderMonths && years < 3
		? `${pluralize(years, 'year')} ${pluralize(remainderMonths, 'month')}`
		: pluralize(years, 'year'));
}

/** Formats the start of the scale unit `unitIndex` units after `origin` into the string shape the given date/datetime property expects. */
export function formatTimelineDateValue(
	origin: Dayjs,
	unitIndex: number,
	scale: TimelineScale,
	propertyType: EntityType | undefined,
): string {
	return dateFromUnitIndex(origin, unitIndex, scale).format(propertyType === EntityType.PropertyDefinitionDatetime ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD');
}

